import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Trash2 } from "lucide-react";
import Checkbox from "shared/Checkbox/Checkbox";
import NcImage from "shared/NcImage/NcImage";
import NcInputNumber from "shared/NcInputNumber";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { Badge } from "components/UI/Badge";
import Truncate from "utils/Truncate";
import formatCurrencyINR from "utils/convertNumbToCurrency";
import { ICartItem } from "store/types/cartTypes";
import { IAttributeImage } from "Interfaces/interface.productsapi";
import { getProductNameWithAttributes } from "utils/AttributeHandlers";
import { CartUpdateActionType } from "@/data/cartActions";
import { titleCase } from "utils/ToTitleCase";

interface KitCartCardProps {
    item: ICartItem;
    index: number;
    isSelected: boolean;
    onSelect: (checked: boolean, productId: string, variantId: string) => void;
    onRemove: (productId: string, variantId: string, productGroupId?: string) => void;
    onQuantityChange: (productId: string, variantId: string, qty: number, actionType: string) => void;
    isRemoving: boolean;
    disabled: boolean;
    getDisplayQuantity: (productId: string, variantId: string) => number;
}

const KitCartCard: React.FC<KitCartCardProps> = ({
    item,
    index,
    isSelected,
    onSelect,
    onRemove,
    onQuantityChange,
    isRemoving,
    disabled,
    getDisplayQuantity,
}) => {

    // Get kit item count
    const kitItemCount = item.productsCount || item.product_group_items?.length || 0;

    // Get image URL
    const imageUrl =
        item.images && Array.isArray(item.images) && typeof item.images?.[0] === "string"
            ? item.images?.[0]
            : (item.images?.[0] as IAttributeImage)?.images?.[0];

    const itemId = item.id || item.productId;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: -100 }}
            transition={{
                duration: 0.25,
                delay: index * 0.05,
                type: "spring",
                stiffness: 100,
                damping: 18,
            }}
            className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-5 border rounded-lg transition-colors w-full ${isSelected
                ? "bg-blue-50 border-blue-200"
                : "hover:bg-gray-50 border-slate-200 dark:border-slate-700"
                }`}
        >
            {/* Mobile Header: Badge + Checkbox / Desktop: Checkbox only */}
            <div className="w-full flex justify-between items-center sm:w-auto sm:self-center bg-gray-50/50 sm:bg-transparent p-2 sm:p-0 rounded-md sm:rounded-none mb-2 sm:mb-0">
                <div className="sm:hidden">
                    <Badge className="whitespace-nowrap bg-blue-600 text-white border-blue-200 flex items-center gap-1 text-xs py-0.5 h-6">
                        Kit <Package className="w-3 h-3" />
                        {kitItemCount} {kitItemCount === 1 ? "Product" : "Products"} Inside
                    </Badge>
                </div>

                <Checkbox
                    name={`select-${itemId}-${item.variantId}`}
                    checked={isSelected}
                    onChange={(e) => onSelect(e, itemId, item.variantId)}
                    className={`scale-110 ${(disabled || isRemoving) && "pointer-events-none"}`}
                />
            </div>

            {/* Content Row: Image + Details */}
            <div className="flex flex-row w-full sm:flex-1 gap-4 items-stretch sm:items-center">
                <Link
                    to={`/kit/${itemId}`}
                    className={`w-1/2 sm:w-14 sm:h-14 relative aspect-square sm:aspect-auto flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 ${(disabled || isRemoving) && "pointer-events-none"
                        }`}
                >
                    <NcImage
                        src={imageUrl}
                        alt={item.name}
                        className="w-full h-full object-contain absolute inset-0 sm:static"
                    />
                </Link>

                <div className="w-1/2 sm:w-auto sm:flex-1 flex flex-col justify-between sm:justify-center">
                    <div>
                        <div className="hidden sm:flex items-center gap-2 mb-1">
                            <Badge className="whitespace-nowrap  bg-blue-600 text-white border-blue-200 flex items-center gap-1">
                                Kit <Package className="w-3 h-3" />
                                {kitItemCount} {kitItemCount === 1 ? "Product" : "Products"} Inside
                            </Badge>
                        </div>
                        <Link
                            to={`/kit/${itemId}`}
                            className={`font-semibold text-slate-900 dark:text-white text-base line-clamp-2 sm:line-clamp-1 ${(disabled || isRemoving) && "pointer-events-none"
                                }`}
                        >
                            <Truncate text={titleCase(item.name)} maxLength={25} />
                        </Link>
                        <div className="text-sm mt-0.5">{getProductNameWithAttributes(item)}</div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 h-fit">
                            {formatCurrencyINR(item.price || 0)}/kit
                        </p>
                    </div>

                    {/* Mobile Quantity Selector */}
                    <div className="block sm:hidden mt-2">
                        <NcInputNumber
                            id={`qty-mobile-${itemId}-${item.variantId}`}
                            productId={itemId}
                            defaultValue={getDisplayQuantity(itemId, item.variantId)}
                            disabled={disabled || isRemoving}
                            min={25}
                            widthClass="w-full"
                            size="sm"
                            onChange={(qty) =>
                                onQuantityChange(
                                    itemId,
                                    item.variantId,
                                    qty,
                                    CartUpdateActionType.CUSTOM_QUANTITY
                                )
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-row w-full sm:w-auto items-center justify-end gap-4 sm:gap-10 mt-2 sm:mt-0 pl-0">
                <div className="hidden sm:block">
                    <NcInputNumber
                        id={`qty-${itemId}-${item.variantId}`}
                        productId={itemId}
                        defaultValue={getDisplayQuantity(itemId, item.variantId)}
                        disabled={disabled || isRemoving}
                        min={25}
                        widthClass="w-[110px] sm:w-[130px]"
                        size="sm"
                        onChange={(qty) =>
                            onQuantityChange(
                                itemId,
                                item.variantId,
                                qty,
                                CartUpdateActionType.CUSTOM_QUANTITY
                            )
                        }
                    />
                </div>
                <div className="flex gap-2 items-center justify-end w-full sm:w-auto">
                    {/* Spacer to align with Mockup button in Product Card */}
                    <div className="w-[100px] hidden sm:block"></div>
                    <ButtonSecondary
                        sizeClass="sm"
                        fontSize="xs"
                        onClick={() => onRemove(itemId, item.variantId, itemId)}
                        variant="cancel"
                        className="!py-2 px-3 !text-sm whitespace-nowrap"
                        loading={isRemoving}
                        icon={<Trash2 className="w-5 h-5" />}
                    > <span className="inline ml-2">Remove</span></ButtonSecondary>
                </div>
            </div>
        </motion.div>
    );
};

export default KitCartCard;
